import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { fileUrl, getGallery, getKingdomInfo } from "../lib/api";
import { ArrowLeft, Heart } from "lucide-react";
import { useLang, t } from "../lib/i18n";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

const CASTLE_BG =
  "https://images.unsplash.com/photo-1761236981261-5ad3e4012470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtYWdpY2FsJTIwZmFpcnklMjB0YWxlJTIwY2FzdGxlJTIwcGluayUyMHNreXxlbnwwfHx8fDE3ODE1OTQ1OTd8MA&ixlib=rb-4.1.0&q=85";

export default function Projector() {
  const lang = useLang();
  const location = useLocation();
  // When opened directly, hand off to the orchestrated Director. The Director
  // iframes this page back with `?embed=1` to render just the static "Anagha's
  // message" slot — so we ONLY keep this static screen alive in embed mode.
  const isEmbed = new URLSearchParams(location.search).get("embed") === "1";
  const [guests, setGuests] = useState([]);
  const [info, setInfo] = useState({ princess_name: "Anagha" });
  const [spot, setSpot] = useState(0);

  useEffect(() => {
    if (!isEmbed) return;
    getKingdomInfo().then(setInfo).catch(() => {});
    const refresh = async () => {
      try {
        const g = await getGallery();
        setGuests(g);
      } catch {
        // ignore
      }
    };
    refresh();
    const tm = setInterval(refresh, 5000);
    return () => clearInterval(tm);
  }, [isEmbed]);

  useEffect(() => {
    const tm = setInterval(() => {
      setSpot((i) => (guests.length ? (i + 1) % guests.length : 0));
    }, 30000);
    return () => clearInterval(tm);
  }, [guests.length]);

  const current = guests[spot];

  // Top-level handoff: if NOT embedded inside Director, redirect to /director
  // so the host gets the full orchestrated schedule (cake countdown, dinner
  // rotation, farewell stats, etc.) instead of just the static "Anagha message".
  if (!isEmbed) return <Navigate to="/director" replace />;

  return (
    <div className="fixed inset-0 overflow-hidden bg-plum-deep" data-testid="projector-mode">
      <div
        className="absolute inset-0 animate-ken-burns"
        style={{ backgroundImage: `url(${CASTLE_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-plum-deep/30 via-plum/45 to-plum-deep/90" />
      <Sparkles count={80} />

      <div className="absolute top-5 right-5 z-30"><LangToggle light /></div>

      <Link
        to="/welcome"
        className="absolute top-5 left-5 z-30 glass rounded-full px-3 py-1.5 flex items-center gap-2 text-plum hover:bg-white/80 transition-colors"
        data-testid="projector-back-link"
      >
        <ArrowLeft size={14} />
        <span className="label-eyebrow" style={TE}>
          {lang === "te" ? "తిరిగి" : "Back"}
        </span>
      </Link>

      <div className="relative w-full h-full flex flex-col">
        <header className="px-12 pt-10 flex items-center justify-between" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>
          <div className="flex items-center gap-3">
            <Heart className="text-[#FFC4D6]" size={24} fill="#FFC4D6" />
            <p className="font-script text-3xl text-blush">{t("welcome_title", lang)}</p>
          </div>
          <p className="label-eyebrow text-blush/80" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{guests.length} {t("df_guests", lang)} · {t("live", lang)}</p>
        </header>

        <main className="flex-1 grid place-items-center px-12">
          {current ? (
            <div
              key={current.id}
              className="text-center animate-[fadeIn_0.9s_ease-out] grid md:grid-cols-[auto_1fr] gap-10 items-center max-w-6xl w-full"
              data-testid="projector-spotlight"
            >
              <div className="relative">
                <div className="absolute -inset-10 rounded-full blur-3xl opacity-60" style={{ background: "radial-gradient(circle, #FCF6BA, transparent 70%)" }} />
                <div className="relative magical-card p-3 sm:p-4">
                  <div className="overflow-hidden w-[340px] h-[340px] sm:w-[440px] sm:h-[440px]" style={{ borderRadius: "32px" }}>
                    <img src={fileUrl(current.character_path)} alt={current.name} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>

              <div className="text-left max-w-xl">
                <p className="label-eyebrow text-blush/80 mb-2" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("proj_new_member", lang)}</p>
                <p className="font-script text-5xl sm:text-6xl text-blush drop-shadow-lg">{current.name}</p>
                <p className="font-display text-2xl sm:text-3xl gold-text mt-2" style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}>{current.loving_title || current.role_title}</p>
                {current.personal_message && (
                  <div className="mt-6 glass-deep rounded-2xl p-5 sm:p-6">
                    <p
                      className="font-display text-lg sm:text-xl text-ink leading-relaxed italic"
                      style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}
                      lang="te"
                    >
                      &ldquo;{current.personal_message}&rdquo;
                    </p>
                    <p className="font-script text-2xl text-plum text-right mt-2" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>— {t("df_princess", lang)} ✦</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-blush" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>
              <p className="font-script text-5xl">{t("proj_waiting", lang)}</p>
              <p className="label-eyebrow mt-4 text-blush/80" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("df_qr_hint", lang)}</p>
            </div>
          )}
        </main>

        <footer className="relative pb-10 px-12 flex justify-center">
          <div className="glass rounded-full py-2 px-6 inline-flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#FFC4D6] animate-pulse" />
            <p className="label-eyebrow text-plum" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("proj_rotates", lang)}</p>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(30px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  );
}
